import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { TestIds } from '@/constants/testIds'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useAtomValue, useSetAtom } from 'jotai'
import { registrationEmailAtom } from '@/state/global'
import { register as registerUser } from '@/services/authentication/register'
import { useForm, useController } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { RegisterSchema, type RegisterFormData } from '@/schema'
import { RegistrationStatus } from '@/types/'
import { initialWorkOrderIdAtom } from '@/state/authentication/login'
import { getRegistrationErrorTranslationKey } from '@/helpers/registration-helpers'
import { formatPhoneNumber } from '@/helpers/phone'
import { logUserAction } from '@/services/dashboard/logUserAction'
import { ClarityEvent, trackClarityEvent } from '@/services/telemetry/clarity'
import { UserActivityAction } from '@/types/userActivity'
import type { UserActivityLogPayload } from '@/types/userActivity'
import { FieldError } from '@/components/FieldError'
import { fieldInputClassName } from '@/helpers/fieldInputClassName'
import { readOnlyRequestAtom } from '@/state/dashboard'

export const Route = createFileRoute('/register')({
  component: Register,
})

function Register() {
  useDocumentTitle('meta.title.register')
  const intl = useIntl()
  const navigate = useNavigate()
  const email = useAtomValue(registrationEmailAtom)
  const setInitialWorkOrderId = useSetAtom(initialWorkOrderIdAtom)
  const setReadOnlyRequest = useSetAtom(readOnlyRequestAtom)
  const [responseErrorMessage, setResponseErrorMessage] = useState<
    string | null
  >(null)

  const handleTermsOfUseClick = () => {
    const activityPayload: UserActivityLogPayload = {
      WorkOrderId: '',
      UserId: email,
      Action: UserActivityAction.TermsOfUse,
    }
    logUserAction(activityPayload)
  }

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterSchema),
    mode: 'onTouched',
  })

  const { field: phoneField } = useController({ name: 'phone', control })

  const onSubmit = async (data: RegisterFormData) => {
    const result = await registerUser({
      emailId: email ?? '',
      phoneNumber: data.phone.replace(/\D/g, ''),
      password: data.password,
    })

    if (!result.isSuccess) {
      setResponseErrorMessage(
        intl.formatMessage(
          { id: getRegistrationErrorTranslationKey(result.errorMessageKey) },
          { email },
        ),
      )
    } else {
      setReadOnlyRequest(null)
      sessionStorage.setItem('isSignedIn', 'true')

      if ((result.data?.workOrderId ?? null) !== null) {
        setInitialWorkOrderId(result.data?.workOrderId ?? null)
      }

      if (
        result.registrationStatus === RegistrationStatus.RegisteredNotVerified
      ) {
        trackClarityEvent(ClarityEvent.VerificationRequired)
        navigate({ to: '/verifyInfo' })
      }
      if (result.registrationStatus === RegistrationStatus.RegisteredVerified) {
        trackClarityEvent(ClarityEvent.VerificationNotRequired)
        navigate({ to: '/dashboard' })
      }
    }
  }

  return (
    <div className="flex items-center justify-center overflow-y-auto mt-16">
      <div className="w-full max-w-md space-y-4 px-4">
        <h2 className="text-2xl font-bold">
          <FormattedMessage id="register.title" />
        </h2>
        <p className="text-sm text-gray-600">{email}</p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              htmlFor="phone"
              className="block text-xs font-bold uppercase tracking-wide mb-1"
            >
              <FormattedMessage id="register.phoneLabel" />
            </label>
            <input
              id="phone"
              type="tel"
              data-testid={TestIds.register.phoneInput}
              value={phoneField.value ?? ''}
              onChange={(e) =>
                phoneField.onChange(formatPhoneNumber(e.target.value))
              }
              onBlur={phoneField.onBlur}
              placeholder={intl.formatMessage({ id: 'login.phone' })}
              className={`w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.phone)}`}
            />
            <FieldError
              testId={TestIds.register.phoneError}
              messageId={errors.phone?.message}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-xs font-bold uppercase tracking-wide mb-1"
            >
              <FormattedMessage id="register.newPasswordLabel" />
            </label>
            <input
              id="password"
              type="password"
              maxLength={30}
              data-testid={TestIds.register.passwordInput}
              {...register('password')}
              placeholder={intl.formatMessage({ id: 'login.newPassword' })}
              className={`w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.password)}`}
            />
            <FieldError
              testId={TestIds.register.passwordError}
              messageId={errors.password?.message}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-xs font-bold uppercase tracking-wide mb-1"
            >
              <FormattedMessage id="register.confirmPasswordLabel" />
            </label>
            <input
              id="confirmPassword"
              type="password"
              maxLength={30}
              data-testid={TestIds.register.confirmPasswordInput}
              {...register('confirmPassword')}
              placeholder={intl.formatMessage({ id: 'login.confirmPassword' })}
              className={`w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none ${fieldInputClassName(!!errors.confirmPassword)}`}
            />
            <FieldError
              testId={TestIds.register.confirmPasswordError}
              messageId={errors.confirmPassword?.message}
            />
          </div>

          <div className="space-y-2">
            <span data-testid={TestIds.register.termsLink}>
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline block"
                onClick={handleTermsOfUseClick}
              >
                <FormattedMessage id="register.viewTermsOfUse" />
              </Link>
            </span>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={TestIds.register.termsCheckbox}
                {...register('agreedToTerms')}
                className="h-4 w-4"
              />
              <FormattedMessage id="register.agreeTermsOfUse" />
            </label>
            <FieldError
              testId={TestIds.register.termsError}
              messageId={errors.agreedToTerms?.message}
            />
          </div>

          <button
            type="submit"
            data-testid={TestIds.register.submitButton}
            disabled={!isValid}
            className="w-full rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FormattedMessage id="login.submit" />
          </button>
          {responseErrorMessage && (
            <div
              data-testid={TestIds.register.responseError}
              role="alert"
              className="mt-1 rounded bg-orange-500 px-3 py-2 text-sm font-semibold text-white"
            >
              {responseErrorMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}
